function djb2Hash(string_: string): number {
  let hash = 5381;
  for (let index = 0; index < string_.length; index++) {
    // eslint-disable-next-line unicorn/prefer-code-point
    hash = (hash << 5) + hash + string_.charCodeAt(index);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

export type MondayItemColumn = {
  id: string;
  title: string;
  type: string;
  value: unknown;
};

export type FileWithHidden = {
  extension: string;
  name: string;
  id: string;
  isImage: boolean;
  url: string;
  hidden: boolean;
};

/**
 * Unified cache for all monday task controller calculations.
 * Handles dependency-based memoization with automatic invalidation.
 * Provides type-safe generic methods for all caching needs.
 */
export class ControllerCache {
  // Separate maps for each cache category to maintain clear organization
  private pinnedColumnsCache = new Map<string, MondayItemColumn[]>();
  private detailColumnsCache = new Map<string, MondayItemColumn[]>();
  private hiddenColumnsCache = new Map<string, MondayItemColumn[]>();
  private imagePreviews = new Map<string, string[]>();
  private filesCache = new Map<string, FileWithHidden[]>();
  private displayedUpdatesCache = new Map<string, unknown[]>();
  private hasMoreUpdatesCache = new Map<string, boolean>();
  private updateHeightsCache = new Map<string, number>();
  private updateYPositionsCache = new Map<string, number[]>();
  private showMoreYCache = new Map<string, number>();
  private visibleSectionsCache = new Map<string, string[]>();
  private sectionYPositionsCache = new Map<string, Map<string, number>>();

  /**
   * Generic memoization helper that handles dependency tracking and cache invalidation.
   * Automatically clears old entries for the same key category.
   * @param storageMap The underlying cache storage
   * @param keyPrefix The prefix for this cache entry (e.g., "pinned", "details")
   * @param dependencies Values that when changed invalidate the cache
   * @param compute Function to compute the value if not cached
   * @returns Cached or newly computed value
   */
  private memoize<T>(storageMap: Map<string, T>, keyPrefix: string, dependencies: unknown[], compute: () => T): T {
    const depsString = JSON.stringify(dependencies);
    const depsHash = djb2Hash(depsString);
    const cacheKey = `${keyPrefix}:${depsHash}`;

    if (!storageMap.has(cacheKey)) {
      // Clear old entries for this key prefix
      for (const cachedKey of storageMap.keys()) {
        if (cachedKey.startsWith(`${keyPrefix}:`)) {
          storageMap.delete(cachedKey);
        }
      }
      storageMap.set(cacheKey, compute());
    }

    return storageMap.get(cacheKey) as T;
  }

  // Column operations
  public getPinnedColumns(dependencies: unknown[], compute: () => MondayItemColumn[]): MondayItemColumn[] {
    return this.memoize(this.pinnedColumnsCache, "pinned", dependencies, compute);
  }

  public getDetailColumns(dependencies: unknown[], compute: () => MondayItemColumn[]): MondayItemColumn[] {
    return this.memoize(this.detailColumnsCache, "detail", dependencies, compute);
  }

  public getHiddenColumns(dependencies: unknown[], compute: () => MondayItemColumn[]): MondayItemColumn[] {
    return this.memoize(this.hiddenColumnsCache, "hidden", dependencies, compute);
  }

  // File operations
  public getImagePreviews(dependencies: unknown[], compute: () => string[]): string[] {
    return this.memoize(this.imagePreviews, "previews", dependencies, compute);
  }

  public getFiles(dependencies: unknown[], compute: () => FileWithHidden[]): FileWithHidden[] {
    return this.memoize(this.filesCache, "files", dependencies, compute);
  }

  // Updates operations
  public getDisplayedUpdates<T extends { id: string }>(dependencies: unknown[], compute: () => T[]): T[] {
    return this.memoize(this.displayedUpdatesCache, "displayed", dependencies, compute) as T[];
  }

  public getHasMoreUpdates(dependencies: unknown[], compute: () => boolean): boolean {
    return this.memoize(this.hasMoreUpdatesCache, "hasMore", dependencies, compute);
  }

  public getUpdateHeight(updateId: string, updateText: string, compute: () => number): number {
    const cacheKey = `height:${updateId}:${updateText}`;
    if (!this.updateHeightsCache.has(cacheKey)) {
      this.updateHeightsCache.set(cacheKey, compute());
    }
    return this.updateHeightsCache.get(cacheKey) as number;
  }

  public getUpdateYPositions(dependencies: unknown[], compute: () => number[]): number[] {
    return this.memoize(this.updateYPositionsCache, "updateYPositions", dependencies, compute);
  }

  public getShowMoreY(dependencies: unknown[], compute: () => number): number {
    return this.memoize(this.showMoreYCache, "showMoreY", dependencies, compute);
  }

  // Layout operations
  public getVisibleSections(dependencies: unknown[], compute: () => string[]): string[] {
    return this.memoize(this.visibleSectionsCache, "visibleSections", dependencies, compute);
  }

  public getSectionYPositions(dependencies: unknown[], compute: () => Map<string, number>): Map<string, number> {
    return this.memoize(this.sectionYPositionsCache, "sectionYPositions", dependencies, compute);
  }

  public clear(): void {
    this.pinnedColumnsCache.clear();
    this.detailColumnsCache.clear();
    this.hiddenColumnsCache.clear();
    this.imagePreviews.clear();
    this.filesCache.clear();
    this.displayedUpdatesCache.clear();
    this.hasMoreUpdatesCache.clear();
    this.updateHeightsCache.clear();
    this.updateYPositionsCache.clear();
    this.showMoreYCache.clear();
    this.visibleSectionsCache.clear();
    this.sectionYPositionsCache.clear();
  }
}
